import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearAuthData, scheduleTokenRefresh, getDefaultRoute, getCurrentUser } from './authUtils';
import { getApiUrl } from './config';
import logger from './utils/logger';
import { LoadingSpinner } from './components/LoadingSpinner';

/**
 * Component to handle OAuth callback and exchange auth code for JWT token
 *
 * Security: Uses auth code exchange pattern to prevent JWT leakage in browser
 * history, server logs, proxy logs, and Referer headers.
 */
export default function AuthCallback() {
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const hasExchanged = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode's double-invocation of effects in development.
    // The backend auth code exchange is single-use (atomic claim on the
    // backend), so a second concurrent call would 401. This ref is deliberately
    // never reset — keeping it true across StrictMode's double-mount makes the
    // second invocation a no-op.
    if (hasExchanged.current) return;
    hasExchanged.current = true;

    const exchangeAuthCode = async () => {
      // Parse URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage(error);
        logger.error('[AuthCallback] Authentication error:', error);
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authentication code received from backend. Check backend logs.');
        return;
      }

      // Exchange auth code for JWT token
      try {
        const response = await fetch(getApiUrl('/api/auth/exchange-code/'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to exchange auth code');
        }

        const data = await response.json();
        const token = data.token;

        if (!token) {
          throw new Error('No token received from exchange endpoint');
        }

        // Clear any existing auth data before storing new token
        clearAuthData();
        // Store token in localStorage
        localStorage.setItem('auth_token', token);
        scheduleTokenRefresh();

        setStatus('success');
        // Brief delay to show success message, then redirect
        setTimeout(() => {
          const user = getCurrentUser();
          navigate(getDefaultRoute(user));
        }, 1000);
      } catch (err) {
        logger.error('[AuthCallback] Error exchanging auth code:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Failed to complete authentication');
      }
    };

    exchangeAuthCode();
    // Intentionally no cleanup function. The hasExchanged ref guard above
    // prevents StrictMode's double-mount from issuing two fetches. Aborting
    // the in-flight fetch on cleanup would cancel the only POST in dev
    // (StrictMode tears down mount #1 before the request leaves the browser,
    // and mount #2 then no-ops on the ref). The auth code is single-use
    // server-side, so letting the fetch finish naturally is safe.
  }, [navigate]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      fontFamily: 'Arial, sans-serif',
    }}>
      {status === 'processing' && (
        <>
          <div style={{ fontSize: '24px', marginBottom: '20px' }}>
            Completing authentication...
          </div>
          <LoadingSpinner label="Processing authentication" className="mx-auto" />
        </>
      )}
      {status === 'success' && (
        <div style={{ fontSize: '24px', color: 'green' }}>
          ✓ Authentication successful! Redirecting...
        </div>
      )}
      {status === 'error' && (
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '30px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✗</div>
          <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>
            Authentication Failed
          </h2>
          {errorMessage && (
            <div style={{
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '25px',
              color: '#721c24',
              fontSize: '14px',
              textAlign: 'left',
              wordBreak: 'break-word',
            }}>
              <strong>Error:</strong> {errorMessage}
            </div>
          )}
          {!errorMessage && (
            <p style={{
              color: '#666',
              marginBottom: '25px',
              fontSize: '16px',
            }}>
              An error occurred during authentication.
            </p>
          )}
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
